import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 overflow-hidden relative">
          {/* Busted bracket SVG — same style as NotFound */}
          <svg
            viewBox="0 0 400 300"
            className="absolute inset-0 w-full h-full opacity-[0.06] pointer-events-none"
            preserveAspectRatio="xMidYMid slice"
          >
            <g stroke="currentColor" strokeWidth="2" fill="none" className="text-foreground">
              <line x1="20" y1="30" x2="60" y2="30" />
              <line x1="20" y1="60" x2="60" y2="60" />
              <line x1="60" y1="30" x2="60" y2="60" />
              <line x1="60" y1="45" x2="100" y2="45" />
              <line x1="20" y1="90" x2="60" y2="90" />
              <line x1="20" y1="120" x2="60" y2="120" />
              <line x1="60" y1="90" x2="60" y2="120" />
              <line x1="60" y1="105" x2="100" y2="105" />
              <line x1="100" y1="45" x2="100" y2="105" />
              <line x1="100" y1="75" x2="140" y2="75" />
            </g>
            <g stroke="currentColor" strokeWidth="2" fill="none" className="text-foreground" opacity="0.5">
              <line x1="380" y1="30" x2="345" y2="35" />
              <line x1="380" y1="60" x2="350" y2="55" />
              <line x1="340" y1="28" x2="338" y2="65" />
              <line x1="385" y1="150" x2="348" y2="155" />
              <line x1="378" y1="185" x2="342" y2="178" />
              <line x1="300" y1="42" x2="302" y2="110" />
              <line x1="298" y1="75" x2="265" y2="80" />
            </g>
            <g stroke="hsl(17, 81%, 54%)" strokeWidth="3" fill="none" opacity="0.3">
              <line x1="155" y1="135" x2="185" y2="135" />
              <line x1="215" y1="138" x2="245" y2="132" />
            </g>
          </svg>

          <div className="relative z-10 flex flex-col items-center text-center max-w-md">
            <div className="flex items-center gap-1 mb-6">
              <span className="text-muted-foreground text-lg font-mono">(</span>
              <span className="font-mono text-7xl sm:text-8xl font-bold tracking-tighter text-destructive">
                Oops
              </span>
              <span className="text-muted-foreground text-lg font-mono">)</span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-semibold text-foreground mb-3">
              Something Went Wrong
            </h1>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-8">
              An unexpected error occurred.
              <br />
              <span className="text-sm">Try reloading the page.</span>
            </p>

            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-8"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
